import { ActionTree, ActionContext } from 'vuex';

import { contextifyClient } from '@/http-client';
import { camelToSnake, snakeToCamel } from '@/utils';
import { extractTextParts } from './sseParser';

import { RootStateI } from '../state';
import { ConversationsStateI, ConversationI, ConversationIntentI } from './state';

const DG_CONTEXTIFY = process.env.VUE_APP_URL_DG_CONTEXTIFY;

let currentAbortController: AbortController | null = null;

// Regex to detect agent-internal section markers like /*PLANNING*/, /*ACTION*/, etc.
const AGENT_MARKER_RE = /^\/\*(PLANNING|ACTION|REASONING|FINAL_ANSWER)\*\/$/;

export const actions: ActionTree<ConversationsStateI, RootStateI> = {
  async createNewSession(
    context: ActionContext<ConversationsStateI, RootStateI>,
    payload: {
      userId: string;
      projectId: string;
      responseMessage: string;
    },
  ): Promise<void> {
    const sessionId = (crypto as Crypto & { randomUUID: () => string }).randomUUID();
    const body = {
      sessionId,
      state: {},
    };
    const { data } = await contextifyClient.post(
      `/apps/agent/users/${payload.userId}/sessions`,
      camelToSnake(body),
    );
    const conversationData: ConversationI = {
      id: sessionId,
      projectId: payload.projectId,
      userId: payload.userId,
      intents: [
        {
          text: '',
          responseMessage: payload.responseMessage,
          done: true,
          chunks: [],
          thoughts: '',
        },
      ],
    };
    context.commit('initConversation', conversationData);
    console.log(data);
    return data;
  },

  // file upload google adk "file_data": map[string]interface{}{ "file_uri": inputData.MediaURL, "mime_type": mimeType, "file_name": inputData.UploadFileName, },

  async sendMessage(
    context: ActionContext<ConversationsStateI, RootStateI>,
    payload: {
      userId: string;
      projectId: string;
      text: string;
      fileData?: { fileUri: string; mimeType: string; fileName: string }[];
    },
  ): Promise<void> {
    context.commit('pushIntent', {
      text: payload.text,
      responseMessage: 'Thinking...',
      done: false,
      chunks: [],
      thoughts: 'thinking...',
    } as ConversationIntentI);

    currentAbortController = new AbortController();

    const parts: any[] = [];
    if (payload.text) {
      parts.push({ text: payload.text });
    }
    if (payload.fileData && payload.fileData.length > 0) {
      payload.fileData.forEach((file) => {
        parts.push({
          fileData: {
            fileUri: file.fileUri,
            mimeType: file.mimeType,
            fileName: file.fileName,
          },
        });
      });
    }

    const response = await fetch(`${DG_CONTEXTIFY}/run_sse`, {
      method: 'POST',
      signal: currentAbortController.signal,
      body: JSON.stringify({
        appName: 'agent',
        userId: String(payload.userId),
        sessionId: context.state.conversation.id,
        newMessage: { role: 'user', parts: camelToSnake(parts) },
        streaming: true,
      }),
      headers: {
        'Content-Type': 'application/json',
        Authorization: `DGTK ${localStorage.getItem('token')}`,
        'Dg-Project-Id': payload.projectId,
      },
    });

    const reader = response.body?.getReader();
    if (!reader) return;

    const decoder = new TextDecoder('utf-8');
    let done = false;
    let buffer = '';
    let isFirstChunk = true;

    const statusMessages = [
      '⚙️ Computing...',
      '⚡ Processing...',
      '🔍 Exploring...',
      '📊 Analyzing...',
      '🤔 Thinking hard...',
      '✨ Generating response...',
      '💪 Working on it...',
      '🎯 Almost there...',
      '🔄 Still computing...',
      '🧠 Organizing thoughts...',
      '✏️ Refining answer...',
      '⏳ Just a moment...',
    ];
    let statusInterval: number | null = null;
    let delayTimer: number | null = null;

    const startStatusUpdates = () => {
      if (statusInterval) return;
      statusInterval = setInterval(() => {
        const randomIndex = Math.floor(Math.random() * statusMessages.length);
        context.commit('updateResponseMessage', {
          responseMessage: statusMessages[randomIndex],
        });
      }, 5000);
    };

    delayTimer = setTimeout(startStatusUpdates, 5000);

    const clearTimers = () => {
      if (delayTimer) {
        clearTimeout(delayTimer);
        delayTimer = null;
      }
      if (statusInterval) {
        clearInterval(statusInterval);
        statusInterval = null;
      }
    };

    /**
     * Processes a single SSE event string (may contain multiple lines).
     * Finds the "data:" line, parses the JSON, and dispatches mutations.
     *
     * The agent sends two formats:
     *   A) Many small events, each with a single part: { text: "token" }
     *   B) One final event with two parts:
     *        [{ text: "...full thought...", "thought" }, { text: "final answer text" }]
     *      Note: "thought" is a bare key with no value — JSON.parse gives it as undefined,
     *      which is falsy, so we check `"thought" in p` instead of `p.thought === true`.
     */
    const processChunk = (chunk: string) => {
      const dataLines = chunk
        .split(/\r?\n/)
        .filter((line) => line.startsWith('data:'));

      if (!dataLines.length) return;

      const payloads = dataLines
        .map((line) => line.replace(/^data:\s*/, '').trim())
        .filter(Boolean)
        .map((line) => {
          try {
            return JSON.parse(line);
          } catch {
            return null;
          }
        })
        .filter(Boolean);

      if (!payloads.length) return;

      payloads.forEach((json: Record<string, unknown>) => {
        if (json.error) {
          if (isFirstChunk) {
            clearTimers();
            context.commit('clearThinkingText');
            isFirstChunk = false;
          }
          context.commit('appendResponseMessage', {
            responseMessage: `\n\n**Error:** ${String(json.error)}`,
          });
          return;
        }

        if (json.author) {
          console.log('json.author->', json.author);
          context.commit('setModelVersion', String(json.author));
        }
        if ((json as Record<string, unknown>).partial === false && !isFirstChunk) return;

        const parsedParts = extractTextParts(json);
        if (!parsedParts.length) return;

        parsedParts.forEach((part) => {
          const { text, isThought, isToolStatus } = part;
          if (!text || typeof text !== 'string') return;

          if (isThought) {
            if ((json as Record<string, unknown>).partial === false) return;
            context.commit('appendThoughts', { text, isToolStatus });
            return;
          }

          if (AGENT_MARKER_RE.test(text.trim())) return;

          if (isFirstChunk) {
            clearTimers();
            context.commit('clearThinkingText');
            isFirstChunk = false;
          }

          context.commit('appendResponseMessage', { responseMessage: text });
        });
      });
    };

    try {
      while (!done) {
        // eslint-disable-next-line no-await-in-loop
        const { value, done: readerDone } = await reader.read();
        done = readerDone;

        buffer += decoder.decode(value, { stream: !done });

        // Split on both \n\n and \r\n\r\n (servers vary)
        const chunks = buffer.split(/\r?\n\r?\n/);
        buffer = chunks.pop() ?? '';

        chunks
          .map((c) => c.trim())
          .filter(Boolean)
          .forEach(processChunk);

        if (done) {
          if (buffer.trim()) {
            processChunk(buffer.trim());
            buffer = '';
          }
          if (isFirstChunk) {
            context.commit('clearThinkingText');
          }
          context.commit('finishIntent');
        }
      }
    } catch (err) {
      if ((err as Error).name === 'AbortError') {
        if (isFirstChunk) context.commit('clearThinkingText');
        context.commit('finishIntent');
        return;
      }
      throw err;
    } finally {
      clearTimers();
      currentAbortController = null;
      context.commit('setThoughts', ''); // clear thoughts panel when done
    }
  },

  stopMessage() {
    currentAbortController?.abort();
    currentAbortController = null;
  },

  async getTokensUsage(
    context: ActionContext<ConversationsStateI, RootStateI>,
    payload: {
      userId: string;
      projectId: string;
      sessionId: string;
    },
  ) {
    const { data } = await contextifyClient.get(`/api/tokens/usage/session/${payload.sessionId}`);
    context.commit('setTokensUsage', snakeToCamel(data));
    return snakeToCamel(data);
  },
};
